<template>
  <b-modal
    v-model="isVisible"
    modal-class="review-type-selection-modal"
    size="md"
    centered
    hide-footer
    title="Select Review Type"
    @hidden="onModalHidden"
  >
    <div class="review-type-options">
      <!-- Text-based Review Option -->
      <div
        class="review-type-card"
        :class="{ selected: selectedType === 'text' }"
        @click="selectType('text')"
      >
        <div class="card-icon">
          <i class="bi bi-file-text"></i>
        </div>
        <div class="card-content">
          <h5>Text-Based Review</h5>
          <p>
            Traditional review format with rich text fields for detailed
            feedback on:
          </p>
          <ul>
            <li>Technical skills</li>
            <li>Tactical awareness</li>
            <li>Physical attributes</li>
            <li>Mental qualities</li>
          </ul>
        </div>
        <div class="card-check">
          <i v-if="selectedType === 'text'" class="bi bi-check-circle-fill"></i>
          <i v-else class="bi bi-circle"></i>
        </div>
      </div>

      <!-- Traffic Light Review Option -->
      <div
        class="review-type-card"
        :class="{ selected: selectedType === 'traffic-light' }"
        @click="selectType('traffic-light')"
      >
        <div class="card-icon traffic-light-icon">
          <span class="rag-dot red"></span>
          <span class="rag-dot amber"></span>
          <span class="rag-dot green"></span>
        </div>
        <div class="card-content">
          <h5>Traffic Light Review (RAG)</h5>
          <p>
            Quick assessment format using Red/Amber/Green ratings for specific
            skills:
          </p>
          <ul>
            <li>{{ skillCounts.technical }} Technical skills</li>
            <li>{{ skillCounts.tactical }} Tactical skills</li>
            <li>{{ skillCounts.physical }} Physical attributes</li>
            <li>{{ skillCounts.mental }} Mental qualities</li>
          </ul>
          <div class="player-type-badge">
            <i class="bi" :class="playerTypeIcon"></i>
            {{ playerTypeLabel }}
          </div>
        </div>
        <div class="card-check">
          <i
            v-if="selectedType === 'traffic-light'"
            class="bi bi-check-circle-fill"
          ></i>
          <i v-else class="bi bi-circle"></i>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="modal-actions">
      <b-button variant="outline-secondary" @click="handleCancel">
        Cancel
      </b-button>
      <b-button
        variant="success"
        :disabled="!selectedType"
        @click="handleConfirm"
      >
        <i class="bi bi-arrow-right"></i>
        Continue
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  detectPlayerType,
  getSkillsForPlayerType,
  PLAYER_TYPES,
} from "@/config/traffic-light-review-config";

export default {
  name: "ReviewTypeSelectionModal",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    playerData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue", "select", "cancel"],
  data() {
    return {
      selectedType: null,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    detectedPlayerType() {
      return detectPlayerType(this.playerData?.position);
    },
    playerTypeLabel() {
      return PLAYER_TYPES[this.detectedPlayerType]?.label || "Outfield Player";
    },
    playerTypeIcon() {
      return this.detectedPlayerType === "goalkeeper"
        ? "bi-person-arms-up"
        : "bi-person-walking";
    },
    skillCounts() {
      const skills = getSkillsForPlayerType(this.detectedPlayerType);
      return {
        technical: skills.technical?.length || 0,
        tactical: skills.tactical?.length || 0,
        physical: skills.physical?.length || 0,
        mental: skills.mental?.length || 0,
      };
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        // Reset selection when modal opens
        this.selectedType = null;
      }
    },
  },
  methods: {
    selectType(type) {
      this.selectedType = type;
    },
    handleConfirm() {
      if (!this.selectedType) return;

      this.$emit("select", {
        reviewType: this.selectedType,
        playerType: this.detectedPlayerType,
      });
      this.isVisible = false;
    },
    handleCancel() {
      this.$emit("cancel");
      this.isVisible = false;
    },
    onModalHidden() {
      this.selectedType = null;
    },
  },
};
</script>

<style scoped>
.review-type-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.review-type-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
}

.review-type-card:hover {
  border-color: #adb5bd;
  background: #f8f9fa;
}

.review-type-card.selected {
  border-color: #28a745;
  background: #f0fff4;
}

.card-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  border-radius: 10px;
  flex-shrink: 0;
}

.card-icon i {
  font-size: 1.5rem;
  color: #495057;
}

.traffic-light-icon {
  flex-direction: column;
  gap: 4px;
  padding: 6px;
}

.rag-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.rag-dot.red {
  background: #dc3545;
}

.rag-dot.amber {
  background: #ffc107;
}

.rag-dot.green {
  background: #28a745;
}

.card-content {
  flex: 1;
}

.card-content h5 {
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  color: #212529;
}

.card-content p {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  color: #6c757d;
}

.card-content ul {
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.85rem;
  color: #495057;
}

.card-content ul li {
  margin-bottom: 0.15rem;
}

.player-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.75rem;
  padding: 0.25rem 0.75rem;
  background: #e9ecef;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #495057;
}

.player-type-badge i {
  font-size: 0.9rem;
}

.card-check {
  flex-shrink: 0;
}

.card-check i {
  font-size: 1.25rem;
}

.review-type-card.selected .card-check i {
  color: #28a745;
}

.review-type-card:not(.selected) .card-check i {
  color: #dee2e6;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

/* Responsive */
@media (max-width: 576px) {
  .review-type-card {
    padding: 1rem;
  }

  .card-icon {
    width: 40px;
    height: 40px;
  }

  .card-icon i {
    font-size: 1.25rem;
  }

  .traffic-light-icon {
    padding: 4px;
  }

  .rag-dot {
    width: 10px;
    height: 10px;
  }

  .card-content h5 {
    font-size: 1rem;
  }

  .card-content p,
  .card-content ul {
    font-size: 0.8rem;
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-actions .btn {
    width: 100%;
  }
}
</style>
